import React, { useState } from 'react';
import { FaEdit, FaTrash, FaPlay, FaTimes } from 'react-icons/fa';
import { BUCKET_LABELS } from './bucketLabels';

export default function ResearchConfigCard({
  agentId,        // when set, delete uses /agents/{agentId}/research/config/{id}
  portfolioId,    // used for the run endpoint; may be null when agent has no assignment
  apiBaseUrl: apiBaseUrlProp,
  authToken,
  config,
  readOnly,       // when true, hides Run button (used in AgentResearchTab)
  onEdit,
  onDeleted,
  onRunStarted,
}) {
  const apiBaseUrl = apiBaseUrlProp || window.location.origin.replace('/web', '');
  const [showRunDialog, setShowRunDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [runtimeDirective, setRuntimeDirective] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const DIRECTIONS_MAX_LENGTH = 2000;

  const handleRunResearch = async () => {
    setError(null);
    setIsSubmitting(true);

    try {
      const headers = { 'Content-Type': 'application/json' };
      if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
      const response = await fetch(`${apiBaseUrl}/portfolio/${portfolioId}/research/run`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          research_config_id: config.id,
          runtime_directive: runtimeDirective || null,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      onRunStarted(data.ticket_id);
      setShowRunDialog(false);
      setRuntimeDirective('');
    } catch (err) {
      console.error('Failed to start research run:', err);
      setError(err.message || 'Failed to start research run');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      const headers = {};
      if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
      const deleteUrl = agentId
        ? `${apiBaseUrl}/agents/${agentId}/research/config/${config.id}`
        : `${apiBaseUrl}/portfolio/${portfolioId}/research/config/${config.id}`;
      const response = await fetch(deleteUrl, {
        method: 'DELETE',
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
      }

      onDeleted();
    } catch (err) {
      console.error('Failed to delete research config:', err);
      alert(`Failed to delete: ${err.message}`);
    }
  };

  const truncatePrompt = (prompt, lines = 3) => {
    const charsPerLine = 80;
    const maxChars = lines * charsPerLine;
    if (prompt.length <= maxChars) return prompt;
    return prompt.substring(0, maxChars - 3) + '...';
  };

  const [isExpanded, setIsExpanded] = useState(false);
  const displayPrompt = isExpanded ? config.methodology_prompt : truncatePrompt(config.methodology_prompt);

  const formatDateTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) + ' ' + date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
      {/* Header with badge */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className={`px-3 py-1 rounded-full text-sm font-medium bg-cyan-900/40 border border-cyan-700/50 text-cyan-300`}>
            {BUCKET_LABELS[config.bucket]}
          </span>
          {config.is_active && (
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-900/40 border border-green-700/50 text-green-300">
              Active
            </span>
          )}
        </div>
      </div>

      {/* Methodology Prompt */}
      <div className="mb-4">
        <h4 className="text-sm font-medium text-slate-300 mb-2">Methodology:</h4>
        <p className="text-slate-300 text-sm leading-relaxed whitespace-pre-wrap">
          {displayPrompt}
        </p>
        {config.methodology_prompt.length > 240 && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="mt-2 text-cyan-400 hover:text-cyan-300 text-sm font-medium"
          >
            {isExpanded ? 'Show less' : 'Show more'}
          </button>
        )}
      </div>

      {/* Timestamps */}
      <div className="flex items-center gap-4 mb-6 text-xs text-slate-400">
        <span>Created: {formatDateTime(config.created_at)}</span>
        <span>Updated: {formatDateTime(config.updated_at)}</span>
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-4 p-3 bg-red-900/30 border border-red-800 rounded-lg">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-red-400 mr-2 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="text-red-300 text-sm">{error}</div>
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex items-center gap-3">
        {!readOnly && (
          <button
            onClick={() => setShowRunDialog(true)}
            disabled={isSubmitting}
            className="flex items-center gap-2 px-4 py-2.5 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FaPlay className="w-4 h-4" />
            Run Research
          </button>
        )}
        <button
          onClick={onEdit}
          disabled={isSubmitting}
          className="flex items-center gap-2 px-4 py-2.5 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg font-medium transition-colors border border-slate-600 disabled:opacity-50"
        >
          <FaEdit className="w-4 h-4" />
          Edit
        </button>
        <button
          onClick={() => setShowDeleteConfirm(true)}
          disabled={isSubmitting}
          className="flex items-center gap-2 px-4 py-2.5 bg-red-900/30 hover:bg-red-900/50 text-red-400 rounded-lg font-medium transition-colors border border-red-800/50 disabled:opacity-50"
        >
          <FaTrash className="w-4 h-4" />
          Delete
        </button>
      </div>

      {/* Run Research Dialog */}
      {!readOnly && showRunDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowRunDialog(false)} />
          <div className="relative bg-slate-800 border border-slate-700 rounded-xl p-6 w-full max-w-lg shadow-2xl">
            <h3 className="text-lg font-semibold text-white mb-4">Run Research</h3>

            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Focus this run on... (optional)
              </label>
              <textarea
                value={runtimeDirective}
                onChange={(e) => setRuntimeDirective(e.target.value)}
                disabled={isSubmitting}
                placeholder="E.g., Pay special attention to tokens launching new mainnet upgrades this week."
                className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2.5 text-slate-200 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-colors font-mono text-sm resize-y"
                rows={4}
                maxLength={DIRECTIONS_MAX_LENGTH}
              />
              <div className="mt-1 text-xs text-slate-400">
                {runtimeDirective.length} / {DIRECTIONS_MAX_LENGTH} characters
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-700/50">
              <button
                onClick={() => setShowRunDialog(false)}
                disabled={isSubmitting}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg font-medium transition-colors border border-slate-600"
              >
                Cancel
              </button>
              <button
                onClick={handleRunResearch}
                disabled={isSubmitting}
                className={`flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg font-medium transition-colors ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Starting...
                  </>
                ) : (
                  <>
                    <FaPlay className="w-4 h-4" />
                    Start Research
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowDeleteConfirm(false)} />
          <div className="relative bg-slate-800 border border-slate-700 rounded-xl p-6 w-full max-w-md shadow-2xl">
            <h3 className="text-lg font-semibold text-white mb-4">Delete Research Config?</h3>
            <p className="text-slate-300 text-sm mb-6">
              Are you sure you want to delete this research config? This action cannot be undone.
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg font-medium transition-colors border border-slate-600"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
